import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, Plus, Trash2, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Admin panel: blocked e-mail addresses + the latest security alerts. */
export default function SecurityBlocklistPanel({ token }) {
  const [rows, setRows] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [q, setQ] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, a] = await Promise.all([
        fetch(`${API_URL}/api/admin/email-blacklist${q ? `?q=${encodeURIComponent(q)}` : ''}`, { headers }),
        fetch(`${API_URL}/api/admin/security-alerts?limit=20`, { headers }),
      ]);
      setRows(b.ok ? (await b.json()).rows || [] : []);
      setAlerts(a.ok ? (await a.json()).rows || [] : []);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, [q, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!email.includes('@')) { toast.error('Enter a valid e-mail'); return; }
    const r = await fetch(`${API_URL}/api/admin/email-blacklist`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, reason }),
    });
    if (r.ok) { toast.success(`${email} blocked`); setEmail(''); setReason(''); load(); }
    else toast.error('Could not block this address');
  };

  const remove = async (addr) => {
    const r = await fetch(`${API_URL}/api/admin/email-blacklist/${encodeURIComponent(addr)}`, {
      method: 'DELETE', headers,
    });
    if (r.ok) { toast.success(`${addr} unblocked`); load(); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="security-blocklist-panel">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 md:p-6">
        <h3 className="text-base md:text-lg font-bold text-slate-800 flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-red-600" /> Blocked e-mails
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          Blocked addresses can neither register nor log in. Disposable providers
          (yopmail, mailinator, 10minutemail…) are already blocked automatically.
        </p>

        <div className="flex flex-wrap gap-2 mt-4">
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            data-testid="blocklist-email"
            className="flex-1 min-w-[200px] px-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)"
            data-testid="blocklist-reason"
            className="flex-1 min-w-[160px] px-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
          <button onClick={add} data-testid="blocklist-add" className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Block
          </button>
        </div>

        <div className="relative mt-3">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search the list"
            data-testid="blocklist-search"
            className="pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg text-xs w-full sm:w-[260px]"
          />
        </div>

        {loading ? (
          <p className="mt-4 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
        ) : rows.length === 0 ? (
          <p className="mt-4 text-sm text-slate-400" data-testid="blocklist-empty">No manually blocked addresses yet.</p>
        ) : (
          <table className="w-full text-sm mt-4">
            <thead>
              <tr className="text-[11px] uppercase text-slate-500 border-b border-slate-200">
                <th className="text-left py-2">E-mail</th>
                <th className="text-left py-2">Reason</th>
                <th className="text-left py-2">Blocked by</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.email} className="border-b border-slate-100" data-testid={`blocklist-row-${r.email}`}>
                  <td className="py-2 font-semibold text-slate-800">{r.email}</td>
                  <td className="py-2 text-slate-500 text-xs">{r.reason || '—'}</td>
                  <td className="py-2 text-slate-500 text-xs">{r.added_by}<br /><span className="text-slate-400">{(r.added_at || '').slice(0, 16).replace('T', ' ')}</span></td>
                  <td className="py-2 text-right">
                    <button onClick={() => remove(r.email)} data-testid={`blocklist-remove-${r.email}`} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 md:p-6">
        <h3 className="text-base font-bold text-slate-800">Security alerts</h3>
        <p className="text-sm text-slate-500 mt-1">
          Same events that are e-mailed to the owner: large recharges, holds for
          review, rate-limit hits and repeated errors on money endpoints.
        </p>
        {alerts.length === 0 ? (
          <p className="mt-4 text-sm text-slate-400" data-testid="alerts-empty">No alerts yet.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {alerts.map((a, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3" data-testid={`alert-row-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold text-sm text-slate-800">{a.subject}</span>
                  <span className="text-[11px] text-slate-400">{(a.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                </div>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 text-xs">
                  {Object.entries(a.rows || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2">
                      <span className="text-slate-500">{k}</span>
                      <span className="font-semibold text-slate-700 text-right">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
